import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { PettyCashService } from '../../features/petty-cash/services/petty-cash.service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { noWhitespaceValidator } from '../../shared/services/validations';

@Component({
  selector: 'app-petty-cash-activity',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    FormsModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './petty-cash-activity.component.html',
  styleUrl: './petty-cash-activity.component.scss',
})
export class AddPettyCashActivity {
  addPettyCashActivityForm: FormGroup;
  pettyCashActivityData: any;
  bankAccountsData: any[] = [];
  private searchSubject = new Subject<string>();
  pettyCashId: any;
  activityId: any;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private route: Router,
    private router: ActivatedRoute,
    private pettyCashService: PettyCashService
  ) {}

  ngOnInit() {
    this.router.queryParamMap.subscribe((queryParams) => {
      this.pettyCashId = queryParams.get('pettyCashId');
      this.activityId = queryParams.get('activityId');
      if(this.activityId){
        this.getActivityById(this.activityId);
      }
    });
    this.initForm();
    this.getAccountsType();
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.getAccountsType(searchTerm);
      });
  }

  initForm() {
    if (this.pettyCashActivityData) {
      this.addPettyCashActivityForm = this.fb.group({
        type: [this.pettyCashActivityData?.type, [Validators.required]],
        description: [this.pettyCashActivityData?.description, [Validators.required, noWhitespaceValidator()]],
        accountId: [this.pettyCashActivityData?.accountId, [Validators.required]],
        amount: [this.pettyCashActivityData?.amount, [Validators.required]],
      });
    } else {
      this.addPettyCashActivityForm = this.fb.group({
        type: ['IN'],
        description: ['', [Validators.required, noWhitespaceValidator()]],
        accountId: ['', [Validators.required]],
        amount: [null, [Validators.required]],
      });
    }
  }

  getActivityById(id: any){
    this.pettyCashService.fetchPettyCashActivityById(Number(id)).subscribe({
      next: (res)=>{
        this.pettyCashActivityData = res;
        this.initForm();
      },
      error: (err)=>{
        console.log(err);
      }
    })
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  getAccountsType(search: string = '') {
    this.pettyCashService.findAccountsByMultipleTypes([], search).subscribe({
      next: (res) => {
        this.bankAccountsData = res;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  allowOnlyNumberWithDecimal(event: KeyboardEvent): void {
    const inputChar = String.fromCharCode(event.keyCode || event.which);

    // Allow digits and decimal point
    if (!/^[0-9.]$/.test(inputChar)) {
      event.preventDefault();
      return;
    }

    // Disallow more than one decimal point
    const input = event.target as HTMLInputElement;
    if (inputChar === '.' && input.value.includes('.')) {
      event.preventDefault();
    }
  }

  blockPaste(event: ClipboardEvent): void {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    if (!/^\d*\.?\d*$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  onSave() {
    if (this.addPettyCashActivityForm.invalid) {
      this.addPettyCashActivityForm.markAllAsTouched();
      return;
    }
    const formValue = this.addPettyCashActivityForm.value;
    const activityInput = {
      ...(this.activityId? { id: Number(this.activityId) } : {}),
      cashAccountId: Number(this.pettyCashId),
      type: formValue.type,
      description: formValue.description,
      accountId: Number(formValue.accountId),
      amount: parseFloat(formValue.amount),
    };
    if(this.activityId){
      this.pettyCashService.updatePettyCashActivity(activityInput, Number(this.activityId)).subscribe({
        next:(res)=>{
          this.toastr.success("Petty cash activity successfully updated")
          this.onCancel()
        },
        error: (err)=>{
          this.toastr.error(err?.message || 'Error in updating activity')
        }
      })
    }
    else{
      this.pettyCashService.createPettyCashActivity(activityInput).subscribe({
        next:(res)=>{
          this.toastr.success("Petty cash activity successfully added")
          this.onCancel()
        },
        error: (err)=>{
          this.toastr.error(err?.message || 'Error in creating activity')
        }
      })
    }
    
  }

  onCancel() {
    this.route.navigate(['petty-cash/petty-cash-details'], {
      queryParams: { pettyCashId: this.pettyCashId },
    });
  }
}
